import { db, storage } from '../config/firebase';
import {
  collection,
  doc,
  addDoc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// Base collection names (will be prefixed with broker ID)
const BASE_COLLECTIONS = {
  TRANSACTIONS: 'transactions',
  BROKERS: 'brokers',
  USERS: 'users',
  TARGET_ACCOUNTS: 'targetAccounts',
  PENDING_REGISTRATIONS: 'pendingRegistrations',
  WHITELISTED_ACCOUNTS: 'whitelistedAccounts',
  AI_SETTINGS: 'aiSettings',
  APP_SETTINGS: 'appSettings',
};

// Default collections (for backward compatibility - maps to savexa)
const COLLECTIONS = {
  TRANSACTIONS: 'transactions',
  BROKERS: 'brokers',
  USERS: 'users',
  TARGET_ACCOUNTS: 'targetAccounts',
  PENDING_REGISTRATIONS: 'pendingRegistrations',
  WHITELISTED_ACCOUNTS: 'whitelistedAccounts',
  AI_SETTINGS: 'aiSettings',
  APP_SETTINGS: 'appSettings',
};

// Get broker-specific collection name
// Format: savexa_transactions, justmarkets_transactions, etc.
export const getBrokerCollection = (brokerId, collectionType) => {
  const normalizedBrokerId = (brokerId || 'savexa').toLowerCase();
  const baseCollection = BASE_COLLECTIONS[collectionType] || collectionType;
  return `${normalizedBrokerId}_${baseCollection}`;
};

// Valid broker IDs
export const VALID_BROKERS = ['savexa', 'justmarkets'];

// Transaction Service - Supports broker-specific collections
export const transactionService = {
  // Create a new transaction with auto-generated ID
  // brokerId parameter determines which collection to use
  create: async (transactionData, brokerId = null) => {
    try {
      const collectionName = brokerId
        ? getBrokerCollection(brokerId, 'TRANSACTIONS')
        : COLLECTIONS.TRANSACTIONS;

      // If transaction has an id, use setDoc to set that specific ID
      if (transactionData.id) {
        const docRef = doc(db, collectionName, transactionData.id);
        await setDoc(docRef, {
          ...transactionData,
          brokerId: brokerId || transactionData.brokerId || 'savexa',
          createdAt: transactionData.createdAt || serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        return { success: true, id: transactionData.id };
      }
      // Otherwise use addDoc for auto-generated ID
      const docRef = await addDoc(collection(db, collectionName), {
        ...transactionData,
        brokerId: brokerId || transactionData.brokerId || 'savexa',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error('Error creating transaction:', error);
      return { success: false, error: error.message };
    }
  },

  // Get transaction by ID
  getById: async (transactionId, brokerId = null) => {
    try {
      const collectionName = brokerId
        ? getBrokerCollection(brokerId, 'TRANSACTIONS')
        : COLLECTIONS.TRANSACTIONS;

      const docRef = doc(db, collectionName, transactionId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { success: true, data: { id: docSnap.id, ...docSnap.data() } };
      }
      return { success: false, error: 'Transaction not found' };
    } catch (error) {
      console.error('Error getting transaction:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all transactions for a specific broker
  getAll: async (brokerId = null) => {
    try {
      const collectionName = brokerId
        ? getBrokerCollection(brokerId, 'TRANSACTIONS')
        : COLLECTIONS.TRANSACTIONS;

      const q = query(
        collection(db, collectionName),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const transactions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      return { success: true, data: transactions };
    } catch (error) {
      console.error('Error getting transactions:', error);
      return { success: false, error: error.message };
    }
  },

  // Get transactions by status for a specific broker
  getByStatus: async (status, brokerId = null) => {
    try {
      const collectionName = brokerId
        ? getBrokerCollection(brokerId, 'TRANSACTIONS')
        : COLLECTIONS.TRANSACTIONS;

      const q = query(
        collection(db, collectionName),
        where('status', '==', status),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const transactions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      return { success: true, data: transactions };
    } catch (error) {
      console.error('Error getting transactions by status:', error);
      return { success: false, error: error.message };
    }
  },

  // Get transactions by broker (from broker-specific collection)
  getByBroker: async (brokerId) => {
    try {
      const collectionName = getBrokerCollection(brokerId, 'TRANSACTIONS');

      const q = query(
        collection(db, collectionName),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const transactions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      return { success: true, data: transactions };
    } catch (error) {
      console.error('Error getting broker transactions:', error);
      return { success: false, error: error.message };
    }
  },

  // Update transaction
  update: async (transactionId, updates, brokerId = null) => {
    try {
      const collectionName = brokerId
        ? getBrokerCollection(brokerId, 'TRANSACTIONS')
        : COLLECTIONS.TRANSACTIONS;

      const docRef = doc(db, collectionName, transactionId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating transaction:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete transaction
  delete: async (transactionId, brokerId = null) => {
    try {
      const collectionName = brokerId
        ? getBrokerCollection(brokerId, 'TRANSACTIONS')
        : COLLECTIONS.TRANSACTIONS;

      const docRef = doc(db, collectionName, transactionId);
      await deleteDoc(docRef);
      return { success: true };
    } catch (error) {
      console.error('Error deleting transaction:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete all transactions for a broker (for resetting)
  deleteAll: async (brokerId = null) => {
    try {
      const collectionName = brokerId
        ? getBrokerCollection(brokerId, 'TRANSACTIONS')
        : COLLECTIONS.TRANSACTIONS;

      const querySnapshot = await getDocs(collection(db, collectionName));
      const deletePromises = querySnapshot.docs.map(document =>
        deleteDoc(doc(db, collectionName, document.id))
      );
      await Promise.all(deletePromises);
      console.log(`Deleted ${querySnapshot.docs.length} transactions from ${collectionName}`);
      return { success: true, count: querySnapshot.docs.length };
    } catch (error) {
      console.error('Error deleting all transactions:', error);
      return { success: false, error: error.message };
    }
  },

  // Real-time listener for transactions (broker-specific)
  subscribeToAll: (callback, brokerId = null) => {
    const collectionName = brokerId
      ? getBrokerCollection(brokerId, 'TRANSACTIONS')
      : COLLECTIONS.TRANSACTIONS;

    console.log(`[Firebase] Setting up transactions subscription for ${collectionName}...`);
    const startTime = Date.now();

    const q = query(
      collection(db, collectionName),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (querySnapshot) => {
      const transactions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      const latency = Date.now() - startTime;
      console.log(`[Firebase] Transactions loaded from ${collectionName}: ${transactions.length} (${latency}ms)`);
      callback(transactions);
    });
  },

  // Real-time listener for pending transactions (broker-specific)
  subscribeToPending: (callback, brokerId = null) => {
    const collectionName = brokerId
      ? getBrokerCollection(brokerId, 'TRANSACTIONS')
      : COLLECTIONS.TRANSACTIONS;

    const q = query(
      collection(db, collectionName),
      where('status', '==', 'pending_review'),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (querySnapshot) => {
      const transactions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      callback(transactions);
    });
  },
};

// Broker Service
export const brokerService = {
  // Get all brokers
  getAll: async () => {
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.BROKERS));
      const brokers = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      return { success: true, data: brokers };
    } catch (error) {
      console.error('Error getting brokers:', error);
      return { success: false, error: error.message };
    }
  },

  // Create broker
  create: async (brokerData) => {
    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.BROKERS), {
        ...brokerData,
        createdAt: serverTimestamp(),
      });
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error('Error creating broker:', error);
      return { success: false, error: error.message };
    }
  },

  // Update broker
  update: async (brokerId, updates) => {
    try {
      const docRef = doc(db, COLLECTIONS.BROKERS, brokerId);
      await updateDoc(docRef, updates);
      return { success: true };
    } catch (error) {
      console.error('Error updating broker:', error);
      return { success: false, error: error.message };
    }
  },
};

// Target Accounts Service
export const targetAccountService = {
  // Get all target accounts
  getAll: async () => {
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.TARGET_ACCOUNTS));
      const accounts = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      return { success: true, data: accounts };
    } catch (error) {
      console.error('Error getting target accounts:', error);
      return { success: false, error: error.message };
    }
  },

  // Create target account
  create: async (accountData) => {
    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.TARGET_ACCOUNTS), {
        ...accountData,
        createdAt: serverTimestamp(),
      });
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error('Error creating target account:', error);
      return { success: false, error: error.message };
    }
  },
};

// Storage Service for receipts, documents, and CNIC images
export const storageService = {
  // Upload receipt image
  uploadReceipt: async (transactionId, file) => {
    try {
      const fileExtension = file.name.split('.').pop();
      const fileName = `receipts/${transactionId}_${Date.now()}.${fileExtension}`;
      const storageRef = ref(storage, fileName);

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      return { success: true, url: downloadURL };
    } catch (error) {
      console.error('Error uploading receipt:', error);
      return { success: false, error: error.message };
    }
  },

  // Upload document image for IBAN verification
  uploadDocument: async (registrationId, file) => {
    try {
      const fileExtension = file.name.split('.').pop();
      const fileName = `documents/${registrationId}_${Date.now()}.${fileExtension}`;
      const storageRef = ref(storage, fileName);

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      return { success: true, url: downloadURL };
    } catch (error) {
      console.error('Error uploading document:', error);
      return { success: false, error: error.message };
    }
  },

  // Upload CNIC image
  uploadCNIC: async (userId, file, side = 'front') => {
    try {
      const fileExtension = file.name ? file.name.split('.').pop() : 'jpg';
      const fileName = `cnic/${userId}_${side}_${Date.now()}.${fileExtension}`;
      const storageRef = ref(storage, fileName);

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      console.log(`[Storage] CNIC ${side} uploaded:`, downloadURL);
      return { success: true, url: downloadURL, side };
    } catch (error) {
      console.error('Error uploading CNIC:', error);
      return { success: false, error: error.message };
    }
  },

  // Upload CNIC from base64 string
  uploadCNICFromBase64: async (userId, base64Data, side = 'front') => {
    try {
      // Extract the base64 content (remove data:image/...;base64, prefix)
      const base64Content = base64Data.includes(',')
        ? base64Data.split(',')[1]
        : base64Data;

      // Determine file extension from mime type
      let fileExtension = 'jpg';
      if (base64Data.includes('data:image/png')) {
        fileExtension = 'png';
      } else if (base64Data.includes('data:image/jpeg') || base64Data.includes('data:image/jpg')) {
        fileExtension = 'jpg';
      }

      // Convert base64 to Blob
      const byteCharacters = atob(base64Content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: `image/${fileExtension}` });

      const fileName = `cnic/${userId}_${side}_${Date.now()}.${fileExtension}`;
      const storageRef = ref(storage, fileName);

      await uploadBytes(storageRef, blob);
      const downloadURL = await getDownloadURL(storageRef);

      console.log(`[Storage] CNIC ${side} uploaded from base64:`, downloadURL);
      return { success: true, url: downloadURL, side };
    } catch (error) {
      console.error('Error uploading CNIC from base64:', error);
      return { success: false, error: error.message };
    }
  },

  // Upload both front and back CNIC images
  uploadCNICBothSides: async (userId, frontFile, backFile) => {
    try {
      const results = await Promise.all([
        storageService.uploadCNIC(userId, frontFile, 'front'),
        storageService.uploadCNIC(userId, backFile, 'back')
      ]);

      const frontResult = results[0];
      const backResult = results[1];

      if (!frontResult.success || !backResult.success) {
        return {
          success: false,
          error: frontResult.error || backResult.error
        };
      }

      return {
        success: true,
        frontUrl: frontResult.url,
        backUrl: backResult.url
      };
    } catch (error) {
      console.error('Error uploading CNIC both sides:', error);
      return { success: false, error: error.message };
    }
  },
};

// Helper function to remove undefined values from an object (Firestore doesn't allow undefined)
const removeUndefinedFields = (obj) => {
  const cleaned = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = value;
    }
  }
  return cleaned;
};

// Pending Registration Service - For accounts awaiting admin approval
// Supports broker-specific collections
export const pendingRegistrationService = {
  // Create a new pending registration
  create: async (registrationData, brokerId = null) => {
    console.log('[Firebase pendingRegistrationService.create] Starting...');
    console.log('[Firebase pendingRegistrationService.create] Data ID:', registrationData.id);
    console.log('[Firebase pendingRegistrationService.create] Data status:', registrationData.status);
    console.log('[Firebase pendingRegistrationService.create] Broker ID:', brokerId || registrationData.brokerId);

    // Remove undefined fields - Firestore doesn't allow undefined values
    const cleanedData = removeUndefinedFields(registrationData);
    console.log('[Firebase pendingRegistrationService.create] Cleaned data keys:', Object.keys(cleanedData));

    const effectiveBrokerId = brokerId || cleanedData.brokerId || 'savexa';
    const collectionName = getBrokerCollection(effectiveBrokerId, 'PENDING_REGISTRATIONS');

    try {
      if (cleanedData.id) {
        console.log('[Firebase pendingRegistrationService.create] Using setDoc with ID:', cleanedData.id);
        const docRef = doc(db, collectionName, cleanedData.id);
        await setDoc(docRef, {
          ...cleanedData,
          brokerId: effectiveBrokerId,
          createdAt: cleanedData.submittedAt || serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        console.log('[Firebase pendingRegistrationService.create] setDoc SUCCESS for ID:', cleanedData.id);
        return { success: true, id: cleanedData.id };
      }
      console.log('[Firebase pendingRegistrationService.create] Using addDoc (auto ID)');
      const docRef = await addDoc(collection(db, collectionName), {
        ...cleanedData,
        brokerId: effectiveBrokerId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      console.log('[Firebase pendingRegistrationService.create] addDoc SUCCESS with ID:', docRef.id);
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error('[Firebase pendingRegistrationService.create] ERROR:', error.message);
      console.error('[Firebase pendingRegistrationService.create] Full error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all pending registrations for a specific broker
  getAll: async (brokerId = null) => {
    try {
      const collectionName = brokerId
        ? getBrokerCollection(brokerId, 'PENDING_REGISTRATIONS')
        : COLLECTIONS.PENDING_REGISTRATIONS;

      const querySnapshot = await getDocs(collection(db, collectionName));
      const allRegistrations = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      // Filter for pending and sort by createdAt locally
      const registrations = allRegistrations
        .filter(reg => reg.status === 'pending')
        .sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || a.submittedAt || 0);
          const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || b.submittedAt || 0);
          return dateB - dateA;
        });
      return { success: true, data: registrations };
    } catch (error) {
      console.error('Error getting pending registrations:', error);
      return { success: false, error: error.message };
    }
  },

  // Get pending registrations by CNIC for a specific broker
  getByCnic: async (cnic, brokerId = null) => {
    try {
      const collectionName = brokerId
        ? getBrokerCollection(brokerId, 'PENDING_REGISTRATIONS')
        : COLLECTIONS.PENDING_REGISTRATIONS;

      const q = query(
        collection(db, collectionName),
        where('cnic', '==', cnic),
        where('status', '==', 'pending')
      );
      const querySnapshot = await getDocs(q);
      const registrations = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      return { success: true, data: registrations };
    } catch (error) {
      console.error('Error getting registrations by CNIC:', error);
      return { success: false, error: error.message };
    }
  },

  // Update pending registration
  update: async (registrationId, updates, brokerId = null) => {
    try {
      const collectionName = brokerId
        ? getBrokerCollection(brokerId, 'PENDING_REGISTRATIONS')
        : COLLECTIONS.PENDING_REGISTRATIONS;

      const docRef = doc(db, collectionName, registrationId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating pending registration:', error);
      return { success: false, error: error.message };
    }
  },

  // Approve registration (moves to whitelisted accounts)
  approve: async (registrationId, brokerId = null) => {
    try {
      const pendingCollectionName = brokerId
        ? getBrokerCollection(brokerId, 'PENDING_REGISTRATIONS')
        : COLLECTIONS.PENDING_REGISTRATIONS;
      const whitelistCollectionName = brokerId
        ? getBrokerCollection(brokerId, 'WHITELISTED_ACCOUNTS')
        : COLLECTIONS.WHITELISTED_ACCOUNTS;

      const docRef = doc(db, pendingCollectionName, registrationId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return { success: false, error: 'Registration not found' };
      }

      const registrationData = docSnap.data();

      // Create whitelisted account
      // Use the SAME ID as the registration for consistency
      const whitelistedAccount = {
        ...registrationData,
        status: 'whitelisted',
        approvedAt: serverTimestamp(),
        originalRegistrationId: registrationId,
      };

      // Use setDoc with the same ID
      const whitelistDocRef = doc(db, whitelistCollectionName, registrationId);
      await setDoc(whitelistDocRef, whitelistedAccount);

      // Update registration status
      await updateDoc(docRef, {
        status: 'approved',
        approvedAt: serverTimestamp(),
      });

      return { success: true };
    } catch (error) {
      console.error('Error approving registration:', error);
      return { success: false, error: error.message };
    }
  },

  // Reject registration
  reject: async (registrationId, reason, brokerId = null) => {
    try {
      const pendingCollectionName = brokerId
        ? getBrokerCollection(brokerId, 'PENDING_REGISTRATIONS')
        : COLLECTIONS.PENDING_REGISTRATIONS;
      const whitelistCollectionName = brokerId
        ? getBrokerCollection(brokerId, 'WHITELISTED_ACCOUNTS')
        : COLLECTIONS.WHITELISTED_ACCOUNTS;

      const docRef = doc(db, pendingCollectionName, registrationId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return { success: false, error: 'Registration not found' };
      }

      const registrationData = docSnap.data();

      // Create rejected account record in whitelisted accounts
      // Use the SAME ID as the registration so we can delete it later
      const rejectedAccount = {
        ...registrationData,
        status: 'rejected',
        rejectedAt: serverTimestamp(),
        rejectionReason: reason,
        originalRegistrationId: registrationId,
      };

      // Use setDoc with the same ID to make deletion work properly
      const whitelistDocRef = doc(db, whitelistCollectionName, registrationId);
      await setDoc(whitelistDocRef, rejectedAccount);

      // Update registration status
      await updateDoc(docRef, {
        status: 'rejected',
        rejectedAt: serverTimestamp(),
        rejectionReason: reason,
      });

      return { success: true };
    } catch (error) {
      console.error('Error rejecting registration:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete registration
  delete: async (registrationId, brokerId = null) => {
    try {
      const collectionName = brokerId
        ? getBrokerCollection(brokerId, 'PENDING_REGISTRATIONS')
        : COLLECTIONS.PENDING_REGISTRATIONS;

      const docRef = doc(db, collectionName, registrationId);
      await deleteDoc(docRef);
      return { success: true };
    } catch (error) {
      console.error('Error deleting registration:', error);
      return { success: false, error: error.message };
    }
  },

  // Real-time listener for pending registrations (broker-specific)
  // Uses server-side filtering for better performance
  subscribeToAll: (callback, brokerId = null) => {
    const collectionName = brokerId
      ? getBrokerCollection(brokerId, 'PENDING_REGISTRATIONS')
      : COLLECTIONS.PENDING_REGISTRATIONS;

    console.log(`[Firebase] Setting up pendingRegistrations subscription for ${collectionName}...`);
    const startTime = Date.now();

    // Use server-side where clause for filtering - much faster than local filtering
    const q = query(
      collection(db, collectionName),
      where('status', '==', 'pending')
    );

    return onSnapshot(q, (querySnapshot) => {
      const pendingRegistrations = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      // Sort locally (sorting is fast, filtering is slow over network)
      pendingRegistrations.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || a.submittedAt || 0);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || b.submittedAt || 0);
        return dateB - dateA;
      });
      const latency = Date.now() - startTime;
      console.log(`[Firebase] Pending registrations from ${collectionName}: ${pendingRegistrations.length} (${latency}ms)`);
      callback(pendingRegistrations);
    }, (error) => {
      console.error('[Firebase] Error in pending registrations subscription:', error);
      callback([]);
    });
  },

  // Get all registrations for a specific CNIC (including pending, approved, and rejected)
  getAllByCnic: async (cnic) => {
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.PENDING_REGISTRATIONS));
      const registrations = querySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
        }))
        .filter(reg => reg.cnic === cnic)
        .sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || a.submittedAt || 0);
          const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || b.submittedAt || 0);
          return dateB - dateA;
        });
      return { success: true, data: registrations };
    } catch (error) {
      console.error('Error getting registrations by CNIC:', error);
      return { success: false, error: error.message };
    }
  },

  // Real-time listener for all registrations by CNIC (pending, approved, rejected)
  subscribeByCnic: (cnic, callback) => {
    return onSnapshot(collection(db, COLLECTIONS.PENDING_REGISTRATIONS), (querySnapshot) => {
      const registrations = querySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
        }))
        .filter(reg => reg.cnic === cnic)
        .sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || a.submittedAt || 0);
          const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || b.submittedAt || 0);
          return dateB - dateA;
        });
      callback(registrations);
    }, (error) => {
      console.error('Error in CNIC registrations subscription:', error);
      callback([]);
    });
  },
};

// Whitelisted Accounts Service - Supports broker-specific collections
export const whitelistedAccountService = {
  // Get all whitelisted accounts for a specific broker
  getAll: async (brokerId = null) => {
    try {
      const collectionName = brokerId
        ? getBrokerCollection(brokerId, 'WHITELISTED_ACCOUNTS')
        : COLLECTIONS.WHITELISTED_ACCOUNTS;

      const querySnapshot = await getDocs(collection(db, collectionName));
      const accounts = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      return { success: true, data: accounts };
    } catch (error) {
      console.error('Error getting whitelisted accounts:', error);
      return { success: false, error: error.message };
    }
  },

  // Get whitelisted accounts by CNIC for a specific broker
  getByCnic: async (cnic, brokerId = null) => {
    try {
      const collectionName = brokerId
        ? getBrokerCollection(brokerId, 'WHITELISTED_ACCOUNTS')
        : COLLECTIONS.WHITELISTED_ACCOUNTS;

      const q = query(
        collection(db, collectionName),
        where('cnic', '==', cnic),
        where('status', '==', 'whitelisted')
      );
      const querySnapshot = await getDocs(q);
      const accounts = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      return { success: true, data: accounts };
    } catch (error) {
      console.error('Error getting accounts by CNIC:', error);
      return { success: false, error: error.message };
    }
  },

  // Create whitelisted account directly (for approved OCR)
  create: async (accountData, brokerId = null) => {
    try {
      const effectiveBrokerId = brokerId || accountData.brokerId || 'savexa';
      const collectionName = getBrokerCollection(effectiveBrokerId, 'WHITELISTED_ACCOUNTS');

      const docRef = await addDoc(collection(db, collectionName), {
        ...accountData,
        brokerId: effectiveBrokerId,
        status: 'whitelisted',
        createdAt: serverTimestamp(),
      });
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error('Error creating whitelisted account:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete whitelisted account (for removing rejected accounts)
  delete: async (accountId, brokerId = null) => {
    try {
      console.log('[Firebase] Attempting to delete whitelisted account:', accountId, 'broker:', brokerId);

      const collectionName = brokerId
        ? getBrokerCollection(brokerId, 'WHITELISTED_ACCOUNTS')
        : COLLECTIONS.WHITELISTED_ACCOUNTS;
      const pendingCollectionName = brokerId
        ? getBrokerCollection(brokerId, 'PENDING_REGISTRATIONS')
        : COLLECTIONS.PENDING_REGISTRATIONS;

      // First try to get the account directly by ID
      const docRef = doc(db, collectionName, accountId);
      let docSnap = await getDoc(docRef);
      let actualDocId = accountId;

      // If not found by direct ID, search by originalRegistrationId
      // (This handles legacy records created with auto-generated IDs)
      if (!docSnap.exists()) {
        console.log('[Firebase] Account not found by direct ID, searching by originalRegistrationId...');
        const q = query(
          collection(db, collectionName),
          where('originalRegistrationId', '==', accountId)
        );
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          console.error('[Firebase] Account not found by ID or originalRegistrationId:', accountId);
          return { success: false, error: 'Account not found' };
        }

        // Use the first matching document
        const foundDoc = querySnapshot.docs[0];
        actualDocId = foundDoc.id;
        docSnap = foundDoc;
        console.log('[Firebase] Found account by originalRegistrationId, actual ID:', actualDocId);
      }

      const accountData = docSnap.data ? docSnap.data() : docSnap;
      console.log('[Firebase] Account data:', accountData);

      // Delete the whitelisted account
      const deleteDocRef = doc(db, collectionName, actualDocId);
      await deleteDoc(deleteDocRef);
      console.log('[Firebase] Whitelisted account deleted:', actualDocId);

      // Also delete the related pending registration if it exists
      const origRegId = accountData.originalRegistrationId || accountId;
      try {
        const pendingDocRef = doc(db, pendingCollectionName, origRegId);
        const pendingSnap = await getDoc(pendingDocRef);
        if (pendingSnap.exists()) {
          await deleteDoc(pendingDocRef);
          console.log('[Firebase] Related pending registration also deleted:', origRegId);
        }
      } catch (pendingError) {
        console.log('[Firebase] No pending registration to delete or already deleted');
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting whitelisted account:', error);
      return { success: false, error: error.message };
    }
  },

  // Real-time listener for whitelisted accounts (broker-specific)
  subscribeToAll: (callback, brokerId = null) => {
    const collectionName = brokerId
      ? getBrokerCollection(brokerId, 'WHITELISTED_ACCOUNTS')
      : COLLECTIONS.WHITELISTED_ACCOUNTS;

    console.log(`[Firebase] Setting up whitelistedAccounts subscription for ${collectionName}...`);
    const startTime = Date.now();

    return onSnapshot(collection(db, collectionName), (querySnapshot) => {
      const accounts = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      const latency = Date.now() - startTime;
      console.log(`[Firebase] Whitelisted accounts loaded from ${collectionName}: ${accounts.length} (${latency}ms)`);
      callback(accounts);
    }, (error) => {
      console.error('Error in whitelisted accounts subscription:', error);
      callback([]);
    });
  },

  // Subscribe to accounts by CNIC (broker-specific)
  subscribeByCnic: (cnic, callback, brokerId = null) => {
    const collectionName = brokerId
      ? getBrokerCollection(brokerId, 'WHITELISTED_ACCOUNTS')
      : COLLECTIONS.WHITELISTED_ACCOUNTS;

    const q = query(
      collection(db, collectionName),
      where('cnic', '==', cnic),
      where('status', '==', 'whitelisted')
    );
    return onSnapshot(q, (querySnapshot) => {
      const accounts = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      callback(accounts);
    }, (error) => {
      console.error('Error in CNIC subscription:', error);
      callback([]);
    });
  },
};

// AI Settings Service - For managing AI API configurations
export const aiSettingsService = {
  // Get all AI configurations
  getAll: async () => {
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.AI_SETTINGS));
      const configs = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      return { success: true, data: configs };
    } catch (error) {
      console.error('Error getting AI settings:', error);
      return { success: false, error: error.message };
    }
  },

  // Get active AI configuration
  getActive: async () => {
    try {
      const q = query(
        collection(db, COLLECTIONS.AI_SETTINGS),
        where('isActive', '==', true)
      );
      const querySnapshot = await getDocs(q);
      if (querySnapshot.docs.length > 0) {
        const doc = querySnapshot.docs[0];
        return { success: true, data: { id: doc.id, ...doc.data() } };
      }
      return { success: true, data: null };
    } catch (error) {
      console.error('Error getting active AI config:', error);
      return { success: false, error: error.message };
    }
  },

  // Create new AI configuration
  create: async (configData) => {
    try {
      // If this is set as active, deactivate others first
      if (configData.isActive) {
        await aiSettingsService.deactivateAll();
      }

      const docRef = await addDoc(collection(db, COLLECTIONS.AI_SETTINGS), {
        ...configData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error('Error creating AI config:', error);
      return { success: false, error: error.message };
    }
  },

  // Update AI configuration
  update: async (configId, updates) => {
    try {
      // If setting as active, deactivate others first
      if (updates.isActive) {
        await aiSettingsService.deactivateAll();
      }

      const docRef = doc(db, COLLECTIONS.AI_SETTINGS, configId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating AI config:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete AI configuration
  delete: async (configId) => {
    try {
      const docRef = doc(db, COLLECTIONS.AI_SETTINGS, configId);
      await deleteDoc(docRef);
      return { success: true };
    } catch (error) {
      console.error('Error deleting AI config:', error);
      return { success: false, error: error.message };
    }
  },

  // Set a configuration as active
  setActive: async (configId) => {
    try {
      // Deactivate all first
      await aiSettingsService.deactivateAll();

      // Set the specified one as active
      const docRef = doc(db, COLLECTIONS.AI_SETTINGS, configId);
      await updateDoc(docRef, {
        isActive: true,
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error) {
      console.error('Error setting active AI config:', error);
      return { success: false, error: error.message };
    }
  },

  // Deactivate all configurations
  deactivateAll: async () => {
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.AI_SETTINGS));
      const updatePromises = querySnapshot.docs.map(document =>
        updateDoc(doc(db, COLLECTIONS.AI_SETTINGS, document.id), {
          isActive: false,
          updatedAt: serverTimestamp(),
        })
      );
      await Promise.all(updatePromises);
      return { success: true };
    } catch (error) {
      console.error('Error deactivating AI configs:', error);
      return { success: false, error: error.message };
    }
  },

  // Real-time listener for AI settings
  subscribeToAll: (callback) => {
    console.log('[Firebase] Setting up aiSettings subscription...');
    const startTime = Date.now();

    return onSnapshot(collection(db, COLLECTIONS.AI_SETTINGS), (querySnapshot) => {
      const configs = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      const latency = Date.now() - startTime;
      console.log(`[Firebase] AI settings loaded: ${configs.length} (${latency}ms)`);
      callback(configs);
    }, (error) => {
      console.error('Error in AI settings subscription:', error);
      callback([]);
    });
  },

  // Test API connection (helper method)
  testConnection: async (config) => {
    // This would be implemented based on the provider
    // For now, return a placeholder
    return { success: true, message: 'Connection test initiated' };
  },
};

// Serial Number Service - For persisting reference number serial counter
// Resets daily - each new day starts from 001
// Supports broker-specific serial numbers
export const serialNumberService = {
  // Get today's date string in DDMMYYYY format
  getTodayDateStr: () => {
    const date = new Date();
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}${month}${year}`;
  },

  // Get current serial number and date for a specific broker
  get: async (brokerId = null) => {
    try {
      const collectionName = brokerId
        ? getBrokerCollection(brokerId, 'APP_SETTINGS')
        : COLLECTIONS.APP_SETTINGS;

      const docRef = doc(db, collectionName, 'serialNumber');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          success: true,
          value: data.value || 0,
          date: data.date || ''
        };
      }
      // If document doesn't exist, return 0
      return { success: true, value: 0, date: '' };
    } catch (error) {
      console.error('Error getting serial number:', error);
      return { success: false, error: error.message, value: 0, date: '' };
    }
  },

  // Increment and return new serial number (resets if date changed)
  increment: async (brokerId = null) => {
    try {
      const collectionName = brokerId
        ? getBrokerCollection(brokerId, 'APP_SETTINGS')
        : COLLECTIONS.APP_SETTINGS;

      const docRef = doc(db, collectionName, 'serialNumber');
      const docSnap = await getDoc(docRef);
      const todayStr = serialNumberService.getTodayDateStr();

      let newValue;
      if (docSnap.exists()) {
        const data = docSnap.data();
        const storedDate = data.date || '';

        // If date changed, reset to 1; otherwise increment
        if (storedDate !== todayStr) {
          newValue = 1;
          console.log(`[SerialNumber] New day detected for ${brokerId || 'default'}, resetting to 1`);
        } else {
          newValue = (data.value || 0) + 1;
        }
      } else {
        newValue = 1;
      }

      await setDoc(docRef, {
        value: newValue,
        date: todayStr,
        brokerId: brokerId || 'savexa',
        updatedAt: serverTimestamp(),
      });

      return { success: true, value: newValue, date: todayStr };
    } catch (error) {
      console.error('Error incrementing serial number:', error);
      return { success: false, error: error.message, value: 0 };
    }
  },

  // Set serial number to a specific value (for initialization or reset)
  set: async (value, date = null, brokerId = null) => {
    try {
      const collectionName = brokerId
        ? getBrokerCollection(brokerId, 'APP_SETTINGS')
        : COLLECTIONS.APP_SETTINGS;

      const docRef = doc(db, collectionName, 'serialNumber');
      const dateStr = date || serialNumberService.getTodayDateStr();
      await setDoc(docRef, {
        value: value,
        date: dateStr,
        brokerId: brokerId || 'savexa',
        updatedAt: serverTimestamp(),
      });
      return { success: true, value, date: dateStr };
    } catch (error) {
      console.error('Error setting serial number:', error);
      return { success: false, error: error.message };
    }
  },
};

// Utility Service for cleaning up old default collections
export const cleanupService = {
  // Delete all documents from the old default (non-prefixed) collections
  deleteOldDefaultCollections: async () => {
    const results = {
      transactions: { deleted: 0, error: null },
      pendingRegistrations: { deleted: 0, error: null },
      whitelistedAccounts: { deleted: 0, error: null },
    };

    try {
      // Delete from old 'transactions' collection
      const transactionsSnapshot = await getDocs(collection(db, 'transactions'));
      if (transactionsSnapshot.docs.length > 0) {
        const transactionDeletes = transactionsSnapshot.docs.map(document =>
          deleteDoc(doc(db, 'transactions', document.id))
        );
        await Promise.all(transactionDeletes);
        results.transactions.deleted = transactionsSnapshot.docs.length;
        console.log(`[Cleanup] Deleted ${results.transactions.deleted} documents from 'transactions'`);
      }
    } catch (error) {
      console.error('[Cleanup] Error deleting transactions:', error);
      results.transactions.error = error.message;
    }

    try {
      // Delete from old 'pendingRegistrations' collection
      const pendingSnapshot = await getDocs(collection(db, 'pendingRegistrations'));
      if (pendingSnapshot.docs.length > 0) {
        const pendingDeletes = pendingSnapshot.docs.map(document =>
          deleteDoc(doc(db, 'pendingRegistrations', document.id))
        );
        await Promise.all(pendingDeletes);
        results.pendingRegistrations.deleted = pendingSnapshot.docs.length;
        console.log(`[Cleanup] Deleted ${results.pendingRegistrations.deleted} documents from 'pendingRegistrations'`);
      }
    } catch (error) {
      console.error('[Cleanup] Error deleting pending registrations:', error);
      results.pendingRegistrations.error = error.message;
    }

    try {
      // Delete from old 'whitelistedAccounts' collection
      const whitelistedSnapshot = await getDocs(collection(db, 'whitelistedAccounts'));
      if (whitelistedSnapshot.docs.length > 0) {
        const whitelistedDeletes = whitelistedSnapshot.docs.map(document =>
          deleteDoc(doc(db, 'whitelistedAccounts', document.id))
        );
        await Promise.all(whitelistedDeletes);
        results.whitelistedAccounts.deleted = whitelistedSnapshot.docs.length;
        console.log(`[Cleanup] Deleted ${results.whitelistedAccounts.deleted} documents from 'whitelistedAccounts'`);
      }
    } catch (error) {
      console.error('[Cleanup] Error deleting whitelisted accounts:', error);
      results.whitelistedAccounts.error = error.message;
    }

    const totalDeleted = results.transactions.deleted +
                         results.pendingRegistrations.deleted +
                         results.whitelistedAccounts.deleted;

    return {
      success: true,
      totalDeleted,
      details: results
    };
  },

  // Get counts of documents in old default collections (for preview before deletion)
  getOldCollectionCounts: async () => {
    const counts = {
      transactions: 0,
      pendingRegistrations: 0,
      whitelistedAccounts: 0,
    };

    try {
      const transactionsSnapshot = await getDocs(collection(db, 'transactions'));
      counts.transactions = transactionsSnapshot.docs.length;
    } catch (error) {
      console.error('[Cleanup] Error counting transactions:', error);
    }

    try {
      const pendingSnapshot = await getDocs(collection(db, 'pendingRegistrations'));
      counts.pendingRegistrations = pendingSnapshot.docs.length;
    } catch (error) {
      console.error('[Cleanup] Error counting pending registrations:', error);
    }

    try {
      const whitelistedSnapshot = await getDocs(collection(db, 'whitelistedAccounts'));
      counts.whitelistedAccounts = whitelistedSnapshot.docs.length;
    } catch (error) {
      console.error('[Cleanup] Error counting whitelisted accounts:', error);
    }

    return {
      success: true,
      total: counts.transactions + counts.pendingRegistrations + counts.whitelistedAccounts,
      counts
    };
  }
};

export default {
  transactionService,
  brokerService,
  targetAccountService,
  storageService,
  pendingRegistrationService,
  whitelistedAccountService,
  aiSettingsService,
  serialNumberService,
  cleanupService,
};
